import React, { useState, useEffect } from "react";  // ← add useEffect here
import { Modal, Button, Row, Col } from "react-bootstrap";
import axios from "axios";

const refTabConfig = {
  shift: {
    label: "Shift",
    endpoint: "shift",
    table: "ShiftRef",
    idField: "ShiftCode",
    fields: [
      { key: "ShiftCode", label: "Shift code", readOnlyOnEdit: true },
      { key: "ShiftName", label: "Shift name" },
      { key: "ShiftType", label: "Shift type" },
    ],
  },
  department: {
    label: "Department",
    endpoint: "department",
    table: "DepartmentRef",
    idField: "DepartmentCode",
    fields: [
      { key: "DepartmentCode", label: "Department code", readOnlyOnEdit: true },
      { key: "DepartmentDescription", label: "Description" },
    ],
  },
  deliveryUnit: {
    label: "Delivery unit",
    endpoint: "deliveryUnit",
    table: "DeliveryUnitRef",
    idField: "DUCode",
    fields: [
      { key: "DUCode", label: "DU code", readOnlyOnEdit: true },
      { key: "DUName", label: "DU name" },
    ],
  },
  positionLevel: {
    label: "Position / level",
    endpoint: "positionLevel",
    table: "PositionLevelRef",
    idField: "PositionLevelID",
    fields: [
      { key: "Position", label: "Position" },
      { key: "Level", label: "Level", type: "number" },
    ],
  },
  project: {
    label: "Project",
    endpoint: "project",
    table: "ProjectRef",
    idField: "ProjectCode",
    fields: [
      { key: "ProjectCode", label: "Project code", readOnlyOnEdit: true },
      { key: "ProjectName", label: "Project name" },
    ],
  },
};

const ROWS_PER_PAGE = 10;

const RefTableManager = () => {
  const [activeRefTab, setActiveRefTab] = useState("shift");
  const [refTableData, setRefTableData] = useState([]);
  const [refTableLoading, setRefTableLoading] = useState(false);
  const [showRefModal, setShowRefModal] = useState(false);
  const [refModalMode, setRefModalMode] = useState("add");
  const [editRefRow, setEditRefRow] = useState({});
  const [refPage, setRefPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const config = refTabConfig[activeRefTab];

  // ── Auto-load on mount ──────────────────────────────────────────────────────
  useEffect(() => {                         // ← now inside the component
    fetchRefTable("shift");
  }, []);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchRefTable = async (tab) => {
    setRefTableLoading(true);
    setRefTableData([]);
    setRefPage(1);
    setSearchQuery("");
    try {
      const res = await axios.get(`/api/ref/${refTabConfig[tab].endpoint}`);
      setRefTableData(res.data);
    } catch (err) {
      alert("Failed to load reference data.");
    } finally {
      setRefTableLoading(false);
    }
  };

  const handleRefTabChange = (tab) => {
    setActiveRefTab(tab);
    fetchRefTable(tab);
  };

  const handleOpenAddRefModal = () => {
    const emptyRow = {};
    config.fields.forEach((f) => (emptyRow[f.key] = ""));
    setEditRefRow(emptyRow);
    setRefModalMode("add");
    setShowRefModal(true);
  };

  const handleOpenEditRefModal = (row) => {
    setEditRefRow({ ...row });
    setRefModalMode("edit");
    setShowRefModal(true);
  };

  const handleRefFieldChange = (e) => {
    const { name, value } = e.target;
    setEditRefRow((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveRefRow = async () => {
    const { endpoint, idField } = config;
    const emptyField = config.fields.find(
      (f) => !editRefRow[f.key] || editRefRow[f.key].toString().trim() === ""
    );
    if (emptyField) {
      alert(`"${emptyField.label}" is required.`);
      return;
    }
    try {
      if (refModalMode === "add") {
        await axios.post(`/api/ref/${endpoint}`, editRefRow, {
          headers: { "Content-Type": "application/json" },
        });
      } else {
        const id = editRefRow[idField];
        await axios.put(`/api/ref/${endpoint}/${id}`, editRefRow, {
          headers: { "Content-Type": "application/json" },
        });
      }
      setShowRefModal(false);
      fetchRefTable(activeRefTab);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save. Please try again.");
    }
  };

  const handleDeleteRefRow = async (row) => {
    const { endpoint, idField, label } = config;
    const id = row[idField];
    if (!window.confirm(`Delete this ${label} record (${id})?`)) return;
    try {
      await axios.delete(`/api/ref/${endpoint}/${id}`);
      fetchRefTable(activeRefTab);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete.");
    }
  };

  const filteredData = searchQuery.trim()
    ? refTableData.filter((row) =>
        config.fields.some((f) =>
          row[f.key]?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : refTableData;

  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (refPage - 1) * ROWS_PER_PAGE,
    refPage * ROWS_PER_PAGE
  );

  return (
    <>
      <hr className="mt-4 mb-4" />
      <h6 className="font-weight-bold text-gray-700 mb-3">
        <i className="fas fa-table mr-2"></i>Manage reference tables
      </h6>

      <ul className="nav nav-pills mb-3" style={{ gap: "6px", flexWrap: "wrap" }}>
        {Object.entries(refTabConfig).map(([key, cfg]) => (
          <li className="nav-item" key={key}>
            <button
              className={`btn btn-sm ${activeRefTab === key ? "btn-primary" : "btn-outline-secondary"}`}
              style={{ borderRadius: "20px", fontSize: "12px", padding: "4px 14px" }}
              onClick={() => handleRefTabChange(key)}
            >
              {cfg.label}
            </button>
          </li>
        ))}
      </ul>

      {refTableLoading ? (
        <div className="text-center py-4 text-muted">
          <i className="fas fa-spinner fa-spin mr-2"></i>Loading {config.label} records...
        </div>
      ) : refTableData.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted small mb-2">No records found. Click a tab above to load, or add a new entry.</p>
           <button className="btn btn-sm btn-outline-primary" onClick={() => fetchRefTable(activeRefTab)}>
            <i className="fas fa-sync-alt mr-1"></i>
           </button>
          <button className="btn btn-sm btn-success" onClick={handleOpenAddRefModal}>
            <i className="fas fa-plus mr-1"></i> Add new {config.label}
          </button>
        </div>
      ) : (
        <div className="table-responsive">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="input-group" style={{ maxWidth: "280px" }}>
              <div className="input-group-prepend">
                <span className="input-group-text"><i className="fas fa-search"></i></span>
              </div>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={`Search ${config.label}...`}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setRefPage(1); }}
              />
              {searchQuery && (
                <div className="input-group-append">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearchQuery(""); setRefPage(1); }}>
                    &times;
                  </button>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <span className="text-muted small">{filteredData.length} record(s)</span>
               <button className="btn btn-sm btn-outline-primary" onClick={() => fetchRefTable(activeRefTab)} title="Reload">
                <i className="fas fa-sync-alt mr-1"></i>
               </button>
              <button className="btn btn-sm btn-success" onClick={handleOpenAddRefModal}>
                <i className="fas fa-plus mr-1"></i> Add new
              </button>
            </div>
          </div>

          <table className="table table-bordered table-hover table-sm">
            <thead>
              <tr>
                {config.fields.map((f) => <th key={f.key}>{f.label}</th>)}
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, i) => (
                  <tr key={i}>
                    {config.fields.map((f) => <td key={f.key}>{row[f.key]}</td>)}
                    <td>
                      <button className="btn btn-xs btn-primary mr-1" onClick={() => handleOpenEditRefModal(row)} title="Edit">
                        <i className="fas fa-pencil-alt"></i>
                      </button>
                      <button className="btn btn-xs btn-danger" onClick={() => handleDeleteRefRow(row)} title="Delete">
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={config.fields.length + 1} className="text-center text-muted py-3">
                    No records match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span className="text-muted small">Page {refPage} of {totalPages}</span>
              <div>
                <button className="btn btn-sm btn-outline-secondary mr-2" onClick={() => setRefPage((p) => Math.max(p - 1, 1))} disabled={refPage === 1}>
                  &laquo; Previous
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setRefPage((p) => Math.min(p + 1, totalPages))} disabled={refPage === totalPages}>
                  Next &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal show={showRefModal} onHide={() => setShowRefModal(false)} centered>
        <Modal.Header>
          <Modal.Title>{refModalMode === "add" ? "Add new" : "Edit"} {config.label}</Modal.Title>
          <Button variant="default" onClick={() => setShowRefModal(false)}>X</Button>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {config.fields.map((f) => (
              <Col key={f.key} md={6}>
                <div className="form-group">
                  <label className="small font-weight-bold">{f.label} <span style={{ color: "red" }}>*</span></label>
                  <input
                    type={f.type === "number" ? "number" : "text"}
                    className="form-control"
                    name={f.key}
                    value={editRefRow[f.key] || ""}
                    onChange={handleRefFieldChange}
                    readOnly={refModalMode === "edit" && f.readOnlyOnEdit}
                    style={refModalMode === "edit" && f.readOnlyOnEdit ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
                  />
                  {refModalMode === "edit" && f.readOnlyOnEdit && (
                    <small className="text-muted">Primary key — cannot be changed.</small>
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRefModal(false)}>Close</Button>
          <Button variant="primary" onClick={handleSaveRefRow}>
            {refModalMode === "add" ? "Add record" : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RefTableManager;